<template>
  <div class="grid grid-cols-1 gap-1 w-11/12 max-w-3xl mx-auto mt-4">
    <div v-for="checklist in checklists" class="border border-gray-200 rounded p-6 flex items-center justify-between">
      <div class="flex-grow">
        <header class="font-bold text-gray-900">{{ checklist.name }}</header>
      </div>
      <div class="flex gap-2 items-center">
        <router-link :to="setupTaskLink(checklist.id)">
          <TButton color="white" label="Setup Tasks" />
        </router-link>

        <ButtonMenu :icon-only="true" :sections="2">
          <template #topmenu>
            <a :class="iconLinkClasses" role="menuitem" @click="onEditClick(checklist)">
              <vue-feather type="edit" :class="iconClasses" />
              Edit Checklist
            </a>
            <a :class="iconLinkClasses" role="menuitem" @click="onCloneClick(checklist)">
              <vue-feather type="copy" :class="iconClasses" />
              Clone Checklist
            </a>
          </template>
          <template #middlemenu>
            <a :class="iconLinkClasses" role="menuitem" @click="onDeleteClick(checklist)">
              <vue-feather type="trash" :class="iconClasses" />
              Delete Checklist
            </a>
          </template>
        </ButtonMenu>
      </div>
    </div>

    <UpsertModal
      form-kind="edit"
      :task-name="taskName"
      :show-modal="showEditModal"
      :initial-name="selectedChecklist?.name"
      @name-input="newChecklistName = $event"
      @action="updateChecklist"
      @close-modal="showEditModal = false"
    />

    <UpsertModal
      form-kind="clone"
      :task-name="taskName"
      :show-modal="showCloneModal"
      @name-input="newChecklistName = $event"
      @action="cloneChecklist"
      @close-modal="showCloneModal = false"
    />

    <DeleteModal :show-modal="showDeleteModal" @close-modal="showDeleteModal = false" @action="deleteChecklist" />

    <ZeroState v-if="!checklists || checklists.length === 0">
      <p>You do not have any {{ taskName.toLowerCase() }} checklists setup yet.</p>
    </ZeroState>
  </div>
</template>

<script>
import { Api } from '../../../../api';
import ButtonMenu from '../../../shared/ButtonMenu.vue';
import ZeroState from '../../../ZeroState.vue';
import UpsertModal from './UpsertModal.vue';
import DeleteModal from './DeleteModal.vue';
import { toast } from 'vue-sonner';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';

export default {
  components: { ZeroState, UpsertModal, DeleteModal, ButtonMenu },
  props: {
    taskName: { type: String, required: true },
    checklists: { type: Array },
    setupTaskLink: { type: Function, required: true },
  },
  setup() {
    const loadingSpinnerStore = useLoadingSpinnerStore();
    return { loadingSpinnerStore };
  },
  data() {
    return {
      showEditModal: false,
      showCloneModal: false,
      showDeleteModal: false,
      selectedChecklist: null,
      newChecklistName: '',
      iconClasses: 'mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500',
      iconLinkClasses:
        'cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900',
    };
  },
  methods: {
    onEditClick(checklist) {
      this.selectedChecklist = checklist;
      this.newChecklistName = checklist.name;
      this.showEditModal = true;
    },
    onCloneClick(checklist) {
      this.selectedChecklist = checklist;
      this.newChecklistName = '';
      this.showCloneModal = true;
    },
    onDeleteClick(checklist) {
      this.selectedChecklist = checklist;
      this.showDeleteModal = true;
    },
    async updateChecklist() {
      const id = this.selectedChecklist.id;
      const name = this.newChecklistName;
      const result = await Api.TaskChecklists.updateTaskChecklist(id, name);

      this.loadingSpinnerStore.hideLocalLoadingSpinner();

      if (result.errors && result.errors.length > 0) {
        toast.error('Checklist save error', {
          action: { label: 'Dismiss' },
          description: result.errors[0],
        });
      } else {
        this.selectedChecklist.name = this.newChecklistName;
        this.showEditModal = false;
        toast.success('Checklist saved', {
          action: { label: 'Dismiss' },
          description: 'Your checklist was successfully saved.  Use the button below to dismiss this alert.',
        });
      }
    },
    async cloneChecklist() {
      const id = this.selectedChecklist.id;
      const name = this.newChecklistName;
      const result = await Api.TaskChecklistClones.create(id, name);

      this.loadingSpinnerStore.hideLocalLoadingSpinner();

      if (result.errors && result.errors.length > 0) {
        toast.error('Checklist clone error', {
          action: { label: 'Dismiss' },
          description: result.errors,
        });
      } else {
        this.$emit('refresh');
        this.showCloneModal = false;
        toast.success('Checklist cloned', {
          action: { label: 'Dismiss' },
          description: 'Your checklist was successfully cloned.  Use the button below to dismiss this alert.',
        });
      }
    },
    async deleteChecklist() {
      try {
        const id = this.selectedChecklist.id;
        const { status } = await Api.TaskChecklists.deleteTaskChecklist(id);

        this.loadingSpinnerStore.hideLocalLoadingSpinner();

        if (status === 'success') {
          this.$emit('refresh');
          this.showDeleteModal = false;
          toast.success('Checklist Deleted', {
            action: { label: 'Dismiss' },
            description: 'Your checklist was successfully deleted.',
          });
        }
      } catch (error) {
        toast.error('Error', {
          action: { label: 'Dismiss' },
          description: 'Failed to delete checklist. Please try again.',
        });
      }
    },
  },
};
</script>
